import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { PayoutRowsTable } from '../components/shopperPayout';
import { ROUTES } from '../constants';
import shopperPayoutService, {
  IN_PROGRESS_STATUSES,
  saveTextAsFile,
} from '../services/shopperPayoutService';

const POLL_INTERVAL_MS = 1500;
const MAX_SILENT_POLL_FAILURES = 3;

const SummaryItem = ({ label, value }) => (
  <div className='flex items-center justify-between gap-4 py-1.5'>
    <span className='text-sm text-gray-600'>{label}</span>
    <span className='text-sm font-semibold text-gray-900 text-right break-all'>
      {value}
    </span>
  </div>
);

/**
 * Import Details — the read-only view of a batch, reached from History Import
 * and from the end of the import wizard.
 */
const ShopperPayoutImportDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const pollFailuresRef = useRef(0);

  const status = batch?.status || null;
  const isPolling = IN_PROGRESS_STATUSES.includes(status);

  const fetchBatch = useCallback(
    async ({ silent = false } = {}) => {
      if (!id) return;
      if (!silent) setLoading(true);

      try {
        setBatch(await shopperPayoutService.getImport(id));
        pollFailuresRef.current = 0;
        setError(null);
      } catch (err) {
        // One dropped poll is not worth a banner; a run of them is.
        pollFailuresRef.current += 1;
        if (!silent || pollFailuresRef.current >= MAX_SILENT_POLL_FAILURES) {
          setError(
            shopperPayoutService.getErrorMessage(
              err,
              'Failed to load the import details.'
            )
          );
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchBatch();
  }, [fetchBatch]);

  // A batch opened from the history list can still be running.
  useEffect(() => {
    if (!isPolling) return undefined;

    let inFlight = false;
    const intervalId = setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        await fetchBatch({ silent: true });
      } finally {
        inFlight = false;
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isPolling, fetchBatch]);

  const handleRowsError = useCallback(message => setError(message), []);

  const handleDownload = useCallback(
    async scope => {
      setDownloading(scope);
      try {
        const { content, fileName } = await shopperPayoutService.downloadRows(
          id,
          scope,
          {
            fallbackFileName: `${
              batch?.batchCode || 'shopper_payout'
            }_${scope}.csv`,
          }
        );
        saveTextAsFile(content, fileName);
      } catch (err) {
        setError(
          shopperPayoutService.getErrorMessage(
            err,
            'Failed to download the CSV file.'
          )
        );
      } finally {
        setDownloading(null);
      }
    },
    [id, batch?.batchCode]
  );

  const uploadedAt = batch?.uploadedAt
    ? moment(batch.uploadedAt).format('DD MMM YYYY HH:mm')
    : '-';

  return (
    <div>
      <div className='mb-6'>
        <button
          type='button'
          onClick={() => navigate(ROUTES.SHOPPER_PAYOUT)}
          className='inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
        >
          <svg
            className='w-4 h-4'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M10 19l-7-7m0 0l7-7m-7 7h18'
            />
          </svg>
          Back
        </button>
      </div>

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2'>
          <svg
            className='w-5 h-5 text-red-400 flex-shrink-0 mt-0.5'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
            />
          </svg>
          <p className='text-sm text-red-700 whitespace-pre-line flex-1'>
            {error}
          </p>
          <button
            type='button'
            onClick={() => setError(null)}
            aria-label='Dismiss error'
            className='text-red-400 hover:text-red-600'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
      )}

      {loading && !batch ? (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-12 text-center text-gray-500'>
          Loading import details...
        </div>
      ) : !batch ? (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-12 text-center text-gray-500'>
          This import could not be found.
        </div>
      ) : (
        <>
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-5 mb-6'>
            <h2 className='text-2xl font-bold text-gray-900'>Import Details</h2>
            <p className='mt-1 text-sm text-gray-500'>{batch.batchCode}</p>
          </div>

          <div className='bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-5 mb-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1 md:divide-x md:divide-gray-200'>
              <div>
                <SummaryItem label='File Name' value={batch.fileName || '-'} />
                <SummaryItem
                  label='Uploaded By'
                  value={batch.uploadedByName || '-'}
                />
                <SummaryItem label='Uploaded At' value={uploadedAt} />
              </div>
              <div className='md:pl-10'>
                <SummaryItem
                  label='Total Shopper Payout'
                  value={shopperPayoutService.formatCount(batch.totalRows)}
                />
                <SummaryItem
                  label='Valid'
                  value={shopperPayoutService.formatCount(batch.validRows)}
                />
                <SummaryItem
                  label='Invalid'
                  value={shopperPayoutService.formatCount(batch.invalidRows)}
                />
              </div>
            </div>
          </div>

          <PayoutRowsTable
            batchId={batch.id}
            summary={batch}
            showRowNumber={false}
            refreshKey={batch.status}
            onError={handleRowsError}
            footer={
              <div className='flex items-center justify-end gap-3'>
                <button
                  type='button'
                  onClick={() => handleDownload('all')}
                  disabled={Boolean(downloading)}
                  className='inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {downloading === 'all' ? 'Downloading...' : 'Download All'}
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                    />
                  </svg>
                </button>
                <button
                  type='button'
                  onClick={() => handleDownload('invalid')}
                  disabled={Boolean(downloading) || !batch.invalidRows}
                  title={
                    batch.invalidRows
                      ? undefined
                      : 'This import has no invalid rows.'
                  }
                  className='inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 disabled:cursor-not-allowed'
                >
                  {downloading === 'invalid'
                    ? 'Downloading...'
                    : 'Download Invalid'}
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                    />
                  </svg>
                </button>
              </div>
            }
          />
        </>
      )}
    </div>
  );
};

export default ShopperPayoutImportDetailPage;
